import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import { formatShortDateFR } from "../lib/format";
import { METHOD_LABELS } from "../lib/constants";
import { nationalityWithFlag } from "../lib/countries";
import FighterImage from "../components/FighterImage";
import StatusBadge from "../components/StatusBadge";
import ShareRecordButton from "../components/ShareRecordButton";
import { useDocumentMeta } from "../lib/useDocumentMeta";

// Phase 11C — hide when the value is missing rather than rendering a "—".
// Stats strip cells always exist because record counters are computed integers
// (0 is still meaningful information there), but the physical/identity cells
// use this component so an empty birth-date/height/etc. simply collapses.
const StatBlock = ({ label, value }) => {
  const empty = value === null || value === undefined || value === "" ||
                (typeof value === "number" && Number.isNaN(value));
  if (empty) return null;
  return (
    <div className="border-t border-neutral-900 py-3 md:py-4 min-w-0">
      <p className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">
        {label}
      </p>
      <p className="mt-1 font-display font-black text-xl md:text-3xl text-white break-words">
        {value}
      </p>
    </div>
  );
};

// Phase 13 — one block for the amateur record, one for the pro record.
// The block collapses when the level has no fight at all (never shows 0-0-0
// for a level a fighter never competed in).
const RecordBlock = ({ label, level, globalRec, buflRec, wuflRec, externalRec, testId }) => {
  const g = globalRec || {};
  const b = buflRec || {};
  const w = wuflRec || {};
  const x = externalRec || {};
  const accent = level === "amateur" ? "text-neutral-300" : "text-gold";
  return (
    <div data-testid={testId}>
      <p className={`text-[10px] font-stats uppercase tracking-[0.3em] ${accent}`}>
        {label}
      </p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 border-t border-b border-neutral-900">
        <RecordCell label="Total" value={g.total || 0} />
        <RecordCell label="Victoires" value={g.wins || 0} />
        <RecordCell label="Défaites" value={g.losses || 0} />
        <RecordCell label="Nuls" value={g.draws || 0} />
        <RecordCell label="KO / TKO" value={g.ko_tko || 0} />
        <RecordCell label="Soumissions" value={g.submissions || 0} />
        <RecordCell label="Décisions" value={g.decisions || 0} />
        <RecordCell label="No contest" value={g.no_contests || 0} />
      </div>
      <p className="mt-3 text-[10px] font-stats uppercase tracking-widest text-neutral-500">
        Détail —{" "}
        <span className="text-neutral-300">BUFL {b.record_str || "0-0-0"}</span>
        {" · "}
        <span className="text-neutral-300">WUFL {w.record_str || "0-0-0"}</span>
        {" · "}
        <span className="text-neutral-300">Externe {x.record_str || "0-0-0"}</span>
      </p>
    </div>
  );
};

const RecordCell = ({ label, value }) => (
  <div className="px-3 py-4 border-r border-neutral-900 last:border-r-0 min-w-0">
    <p className="text-[9px] font-stats uppercase tracking-widest text-neutral-500">
      {label}
    </p>
    <p className="mt-1 font-display font-black text-2xl md:text-4xl text-white leading-none">
      {value}
    </p>
  </div>
);

export default function FighterProfile() {
  const { slug } = useParams();
  const [f, setF] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJson(`/fighters/${slug}`);
        setF(data);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  const nf = f?.next_fight;
  const nfOpp =
    nf?.fighter_a?.id === f?.id ? nf?.fighter_b : nf?.fighter_a;

  const fighterName = f
    ? `${f.first_name}${f.last_name ? ` ${f.last_name}` : ""}`
    : "";
  useDocumentMeta({
    title: fighterName ? `${fighterName.toUpperCase()}${f?.nickname ? ` "${f.nickname}"` : ""}` : null,
    description: f
      ? ["Combattant MMA", f.display_record?.record_str || f.global_record?.record_str || f.record?.record_str, f.city].filter(Boolean).join(" · ")
      : "",
    image: f?.photo_url ? assetUrl(f.photo_url) : null,
  });

  // -------- Phase 13 — records amateur / pro --------
  // Grouped in a single useMemo so the derived record snapshot only recomputes
  // when the underlying fighter payload changes (not on every render).
  const recordSnapshot = useMemo(() => {
    const records = f?.records || {};
    const amateur = records.amateur || null;
    const pro = records.pro || null;
    const unqualified = records.unqualified?.count || 0;
    const externalFights = Array.isArray(f?.external_fights) ? f.external_fights : [];

    const isNonEmpty = (r) => !!r && (r.global?.total || 0) > 0;
    const hasAmateur = isNonEmpty(amateur);
    const hasPro = isNonEmpty(pro);

    // Primary hero record: use `primary_level` when at least one record exists,
    // else fall back to the legacy BUFL record so returning users still see
    // something on empty rosters (0-0-0).
    const primaryLevel = records.primary_level || (f?.amateur_or_pro === "amateur" ? "amateur" : "professionnel");
    const primarySide = primaryLevel === "amateur" ? amateur : pro;
    const primaryLabel = primaryLevel === "amateur" ? "Record amateur" : "Record MMA";
    const primaryRecordStr =
      (hasAmateur || hasPro)
        ? (primarySide?.global?.record_str || "0-0-0")
        : (f?.global_record?.record_str || f?.record?.record_str || "0-0-0");
    const primaryBuflStr =
      (hasAmateur || hasPro)
        ? primarySide?.bufl?.record_str
        : f?.record?.record_str;
    const primaryWuflStr =
      (hasAmateur || hasPro)
        ? (primarySide?.wufl?.total > 0 ? primarySide.wufl.record_str : null)
        : ((f?.wufl_record?.wins + f?.wufl_record?.losses + f?.wufl_record?.draws) > 0 ? f?.wufl_record?.record_str : null);

    return {
      records, amateur, pro, unqualified, externalFights,
      hasAmateur, hasPro,
      primaryLevel, primarySide, primaryLabel,
      primaryRecordStr, primaryBuflStr, primaryWuflStr,
    };
  }, [f]);

  if (notFound) {
    return (
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 min-h-[60vh]">
        <p className="font-stats text-xs uppercase tracking-[0.3em] text-gold">
          404
        </p>
        <h1 className="mt-6 font-display font-black text-5xl md:text-8xl text-white leading-none">
          COMBATTANT INTROUVABLE
        </h1>
        <Link
          to="/fighters"
          className="inline-flex items-center gap-3 mt-10 px-6 py-3 border border-white text-white font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors duration-200"
        >
          Voir la base →
        </Link>
      </section>
    );
  }

  if (!f) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
          Chargement…
        </p>
      </div>
    );
  }

  // -------- Phase 13 — records amateur / pro (via memo above) --------
  const { records, amateur, pro, unqualified, externalFights,
    hasAmateur, hasPro,
    primaryLevel, primarySide, primaryLabel,
    primaryRecordStr, primaryBuflStr, primaryWuflStr } = recordSnapshot;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: fighterName,
    alternateName: f.nickname,
    image: f.photo_url ? assetUrl(f.photo_url) : undefined,
    nationality: f.nationality,
    birthDate: f.birth_date,
    height: f.height_cm ? `${f.height_cm} cm` : undefined,
    memberOf: { "@type": "SportsOrganization", name: "BUFL — Burkina United Fight League" },
  };

  return (
    <div data-testid="fighter-profile-page">
      {/* Bloc QA #2 — JSON-LD safe injection : `<` → `\u003c` empêche un
          champ texte utilisateur de terminer prématurément la balise
          <script> via `</script>`. Google/Bing parsent le JSON identiquement. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }} />
      {/* HERO */}
      <section className="relative isolate overflow-hidden border-b border-neutral-900 w-full max-w-full">
        {f.photo_url && (
          <div className="absolute inset-0 -z-10 opacity-30 overflow-hidden">
            <img
              src={assetUrl(f.photo_url)}
              alt=""
              aria-hidden
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover blur-2xl"
            />
            <div className="absolute inset-0 bg-black/70" />
          </div>
        )}

        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-10 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 relative">
          {/* Left: Portrait */}
          <div className="lg:col-span-5 relative order-2 lg:order-1">
            <FighterImage
              src={assetUrl(f.photo_url)}
              alt={f.first_name}
              className="aspect-[3/4] w-full"
              imgClassName="object-cover object-top"
            />
            <div className="absolute -bottom-3 -right-3 hidden md:block">
              <span className="font-display font-black text-[10rem] text-white/5 leading-none">
                {String(1).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* Right: Info */}
          <div className="lg:col-span-7 order-1 lg:order-2 flex flex-col justify-end">
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={f.status} data-testid="profile-status" />
              {f.league && (
                <span className="text-[10px] font-stats uppercase tracking-widest text-neutral-500 border border-neutral-800 px-2 py-0.5">
                  {f.league.abbreviation}
                </span>
              )}
              {(f.nationality_flag || f.city) && (
                <span className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                  {[f.nationality_flag, f.city].filter(Boolean).join(" ")}
                </span>
              )}
            </div>

            <h1
              className="mt-4 font-display font-black text-white leading-[0.85] break-words max-w-full"
              style={{ fontSize: "clamp(3rem, 12vw, 11rem)" }}
              data-testid="profile-name"
            >
              {f.first_name}
              {f.last_name && (
                <>
                  {" "}
                  <span className="text-neutral-500">{f.last_name}</span>
                </>
              )}
            </h1>
            {f.nickname && (
              <p className="mt-2 text-gold italic text-lg md:text-xl">
                &quot;{f.nickname}&quot;
              </p>
            )}

            <div className="mt-6 flex items-baseline gap-6 flex-wrap">
              <div>
                <p className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                  {primaryLabel}
                </p>
                <p
                  className="font-display font-black text-6xl md:text-8xl text-white leading-none"
                  data-testid="profile-record"
                >
                  {primaryRecordStr}
                </p>
                {primaryBuflStr && (
                  <p className="mt-2 text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                    dont BUFL : <span className="text-neutral-300">{primaryBuflStr}</span>
                    {primaryWuflStr ? (
                      <> · dont WUFL : <span className="text-neutral-300">{primaryWuflStr}</span></>
                    ) : null}
                  </p>
                )}
                {/* Phase B — bouton Partage WhatsApp, sobre, placé sous
                    le record dans le hero. Sur mobile → feuille de partage
                    native. Sur desktop → wa.me direct. */}
                <div className="mt-4">
                  <ShareRecordButton
                    firstName={f.first_name}
                    lastName={f.last_name}
                    nickname={f.nickname}
                    recordStr={primaryRecordStr}
                    testId="fighter-share-record"
                  />
                </div>
              </div>
              {f.weight_class?.name ? (
                <div className="text-neutral-600">
                  <p className="text-[10px] font-stats uppercase tracking-widest">
                    Catégorie
                  </p>
                  <p className="mt-2 font-display font-bold text-lg md:text-2xl text-neutral-300 uppercase">
                    {f.weight_class.name.split(" (")[0]}
                  </p>
                  {(f.division_rank || f.p4p_rank) ? (
                    <div
                      className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1"
                      data-testid="profile-rank-strip"
                    >
                      {f.division_rank && (
                        <span
                          data-testid="profile-division-rank"
                          className="inline-flex items-center gap-1.5 text-[10px] font-stats uppercase tracking-widest text-gold"
                        >
                          <span className="font-display font-black text-base">
                            #{f.division_rank}
                          </span>
                          {f.weight_class.name.split(" (")[0]}
                        </span>
                      )}
                      {f.p4p_rank && (
                        <span
                          data-testid="profile-p4p-rank"
                          className="inline-flex items-center gap-1.5 text-[10px] font-stats uppercase tracking-widest text-neutral-400"
                        >
                          <span className="font-display font-black text-base text-neutral-200">
                            #{f.p4p_rank}
                          </span>
                          P4P
                        </span>
                      )}
                    </div>
                  ) : (
                    /* Phase R2 — no rank at all → fighter is NR
                       (never fought a BUFL bout in his current bucket).
                       Public label per client spec. */
                    <div
                      className="mt-3"
                      data-testid="profile-rank-nr"
                    >
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                        <span className="font-display font-black text-base text-neutral-400">
                          NR
                        </span>
                        NON CLASSÉ
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                /* Phase R2 (Fév 2026) — fighter has no weight class at all
                   (roster incomplet), on affiche quand même le badge NR
                   en tête pour que le statut public soit explicite plutôt
                   qu'implicite par absence. */
                <div className="text-neutral-600">
                  <p className="text-[10px] font-stats uppercase tracking-widest">
                    Catégorie
                  </p>
                  <p
                    className="mt-2 font-display font-bold text-lg md:text-2xl text-neutral-500 uppercase"
                    data-testid="profile-no-weight-class"
                  >
                    Non renseignée
                  </p>
                  <div
                    className="mt-3"
                    data-testid="profile-rank-nr"
                  >
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                      <span className="font-display font-black text-base text-neutral-400">
                        NR
                      </span>
                      NON CLASSÉ
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP — Amateur / Pro séparés (Phase 13) */}
      <section className="border-b border-neutral-900">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14 space-y-10">

          {hasAmateur && (
            <RecordBlock
              label="Record amateur (carrière complète)"
              level="amateur"
              globalRec={amateur.global}
              buflRec={amateur.bufl}
              wuflRec={amateur.wufl}
              externalRec={amateur.external}
              testId="stats-amateur"
            />
          )}

          {hasPro && (
            <RecordBlock
              label="Record professionnel (carrière complète)"
              level="pro"
              globalRec={pro.global}
              buflRec={pro.bufl}
              wuflRec={pro.wufl}
              externalRec={pro.external}
              testId="stats-pro"
            />
          )}

          {!hasAmateur && !hasPro && (
            <p className="text-sm text-neutral-500" data-testid="stats-empty">
              Aucun combat officiel enregistré à ce jour.
              {unqualified > 0 && (
                <>{" "}
                  <span className="text-orange-400">
                    ({unqualified} combat{unqualified > 1 ? "s" : ""} enregistré{unqualified > 1 ? "s" : ""} sans qualification amateur/pro — l&apos;équipe éditoriale les qualifiera prochainement.)
                  </span>
                </>
              )}
            </p>
          )}

          {/* Combats externes — liste publique */}
          {externalFights.length > 0 && (
            <div data-testid="external-fights-list">
              <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold mb-3">
                Combats externes (hors BUFL / WUFL)
              </p>
              <ul className="divide-y divide-neutral-900 border-y border-neutral-900">
                {externalFights.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-baseline gap-3 py-3 text-sm">
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-black tracking-widest ${x.result === "V" ? "bg-emerald-950/60 text-emerald-400 border border-emerald-800" : x.result === "D" ? "bg-red-950/60 text-red-400 border border-red-900" : "bg-neutral-900 text-neutral-400 border border-neutral-800"}`}>
                      {x.result}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-gold font-stats">
                      {x.level === "amateur" ? "Amateur" : "Pro"}
                    </span>
                    <span className="text-white font-medium">{x.opponent_name || "—"}</span>
                    {x.organization && <span className="text-neutral-500 text-xs">· {x.organization}</span>}
                    {x.event_date && <span className="text-neutral-600 text-xs">· {x.event_date}</span>}
                    {x.method && <span className="text-neutral-500 text-xs">· {x.method}</span>}
                    {x.round && <span className="text-neutral-600 text-xs">· R{x.round}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </section>

      {/* TALE OF THE TAPE */}
      <section className="border-b border-neutral-900">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
              Fiche officielle
            </p>
            <h2 className="mt-4 font-display font-black text-3xl md:text-5xl text-white leading-none">
              L&apos;IDENTITÉ
            </h2>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
            <StatBlock label="Prénom" value={f.first_name} />
            <StatBlock label="Nom" value={f.last_name} />
            <StatBlock label="Surnom" value={f.nickname} />
            <StatBlock label="Âge" value={f.age ? `${f.age} ANS` : null} />
            <StatBlock label="Ville" value={f.city} />
            <StatBlock
              label="Nationalité"
              value={nationalityWithFlag(f.nationality, f.nationality_flag)}
            />
            <StatBlock label="Taille" value={f.height_cm ? `${f.height_cm} CM` : null} />
            <StatBlock label="Allonge" value={f.reach_cm ? `${f.reach_cm} CM` : null} />
            <StatBlock label="Poids" value={f.current_weight_kg ? `${f.current_weight_kg} KG` : null} />
            <StatBlock label="Stance" value={f.stance} />
            <StatBlock label="Académie" value={f.academy?.name} />
          </div>
        </div>
      </section>

      {/* BIO (Phase 7 — AI or manual, admin approved by save) */}
      {f.bio && f.bio.trim() && (
        <section className="border-b border-neutral-900" data-testid="fighter-bio-section">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            <div className="lg:col-span-4">
              <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
                Portrait
              </p>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl text-white leading-none">
                BIO
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-neutral-300 text-base md:text-lg leading-relaxed whitespace-pre-wrap" data-testid="fighter-bio-text">
                {f.bio}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* NEXT FIGHT */}
      {nf && (
        <section className="border-b border-neutral-900 bg-neutral-950/60" data-testid="profile-next-fight">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
              Prochain combat
            </p>
            <h2 className="mt-4 font-display font-black text-3xl md:text-5xl text-white leading-none">
              NEXT FIGHT
            </h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-11 items-stretch gap-4">
              <div className="md:col-span-5 border border-neutral-900 bg-black p-6 flex items-end aspect-[4/3] relative overflow-hidden">
                <FighterImage
                  src={assetUrl(f.photo_url)}
                  alt={f.first_name}
                  className="absolute inset-0"
                  imgClassName="object-cover object-top opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="relative">
                  <p className="text-xs font-stats uppercase tracking-widest text-neutral-400">
                    {f.display_record?.record_str || f.global_record?.record_str || f.record?.record_str}
                  </p>
                  <p className="mt-1 font-display font-black text-4xl md:text-6xl text-white leading-none">
                    {f.last_name || f.first_name}
                  </p>
                </div>
              </div>

              <div className="md:col-span-1 flex items-center justify-center py-6 md:py-0">
                <span className="font-display font-black text-5xl md:text-7xl text-gold">VS</span>
              </div>

              <Link
                to={`/fighters/${nfOpp?.slug}`}
                className="md:col-span-5 border border-neutral-900 bg-black p-6 flex items-end aspect-[4/3] relative overflow-hidden group"
                data-testid="next-fight-opponent"
              >
                <FighterImage
                  src={assetUrl(nfOpp?.photo_url)}
                  alt={nfOpp?.first_name}
                  className="absolute inset-0"
                  imgClassName="object-cover object-top opacity-70 group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="relative md:text-right w-full">
                  <p className="text-xs font-stats uppercase tracking-widest text-neutral-400">
                    {nfOpp?.display_record?.record_str || nfOpp?.global_record?.record_str || nfOpp?.record?.record_str}
                  </p>
                  <p className="mt-1 font-display font-black text-4xl md:text-6xl text-white leading-none">
                    {nfOpp?.last_name || nfOpp?.first_name}
                  </p>
                </div>
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-stats uppercase tracking-widest text-neutral-500">
                  {nf.event?.name}
                </p>
                <p className="mt-1 font-display font-bold text-white uppercase text-lg">
                  {formatShortDateFR(nf.event?.date)} · {nf.event?.venue} · {nf.event?.city}
                </p>
              </div>
              <Link
                to={`/fights/${nf.slug}`}
                data-testid="next-fight-cta"
                className="inline-flex items-center gap-3 px-6 py-3 bg-white text-black font-display uppercase tracking-widest text-sm hover:bg-gold transition-colors duration-200"
              >
                Voir le combat <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* HISTORY */}
      <section data-testid="profile-history">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
            Historique complet
          </p>
          <h2 className="mt-4 font-display font-black text-3xl md:text-5xl text-white leading-none">
            FIGHT HISTORY
          </h2>

          {(!f.history || f.history.length === 0) ? (
            <div className="mt-8 border border-neutral-900 line-grid py-16 px-6 text-center">
              <p className="font-display font-black text-2xl md:text-4xl text-neutral-600">
                AUCUN COMBAT ENREGISTRÉ
              </p>
            </div>
          ) : (
            <div className="mt-8 border-t border-neutral-900">
              {f.history.map((h) => (
                <Link
                  key={h.fight_id}
                  to={`/fights/${h.fight_slug}`}
                  data-testid={`history-row-${h.fight_slug}`}
                  className="grid grid-cols-12 gap-2 md:gap-3 items-center py-4 border-b border-neutral-900 hover:bg-neutral-950/60 transition-colors duration-200 group"
                >
                  <div className="col-span-3 md:col-span-1 font-stats text-[10px] md:text-sm text-neutral-500 uppercase tracking-widest min-w-0 truncate">
                    {formatShortDateFR(h.date)}
                  </div>
                  <div className="col-span-3 md:col-span-2 min-w-0">
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[9px] md:text-xs font-stats uppercase tracking-widest border truncate ${
                        h.outcome === "WIN"
                          ? "border-burkina-green/60 text-burkina-green"
                          : h.outcome === "LOSS"
                          ? "border-burkina-red/60 text-burkina-red"
                          : "border-neutral-700 text-neutral-500"
                      }`}
                    >
                      {h.outcome === "WIN" ? "WIN" : h.outcome === "LOSS" ? "LOSS" : h.outcome}
                    </span>
                  </div>
                  <div className="col-span-6 md:col-span-4 font-display font-bold text-white uppercase text-sm md:text-lg min-w-0 truncate">
                    VS {h.opponent?.first_name}
                  </div>
                  <div className="col-span-8 md:col-span-2 font-stats text-[10px] md:text-sm text-neutral-400 uppercase tracking-widest min-w-0 truncate">
                    {METHOD_LABELS[h.method] || h.method}
                  </div>
                  <div className="col-span-4 md:col-span-2 font-stats text-[10px] md:text-sm text-neutral-500 uppercase tracking-widest text-right md:text-left min-w-0 truncate">
                    {[h.round ? `R${h.round}` : null, h.time].filter(Boolean).join(" · ")}
                  </div>
                  <div className="hidden md:block md:col-span-1 text-right text-neutral-500 group-hover:text-white transition-colors duration-200">
                    →
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
